/**
 * Telegram WebApp authentication — Этап 4 backend (2026-05-25).
 *
 * Frontend часть:
 *   1. На app mount проверяем `window.Telegram.WebApp.initData`
 *   2. Если есть — POST /auth/tg с этой signed string
 *   3. Backend верифицирует HMAC по BOT_TOKEN, возвращает user info
 *   4. Сохраняем tg_user_id + first_name в localStorage (`sai-tg-id-v1`)
 *      — для future cloud sync через TG identity вместо anon_id
 *
 * Gates:
 *   - Только если isTelegramRuntime() — иначе skip
 *   - VITE_API_BASE должен быть set
 *
 * Failure modes:
 *   - Bot не configured (503) → ловим, ничего не делаем, ждём JB
 *   - Invalid signature (401) → unlikely в проде, log warn
 *   - Network error → silent drop, повторим на следующем mount
 *
 * Storage:
 *   - `sai-tg-id-v1` → { tg_user_id, first_name, verified_at }
 *   - Не persist auth_date или hash — нет нужды, верифицирует сервер
 */

interface TgWebAppGlobal {
  readonly initData: string;
  readonly initDataUnsafe?: { user?: { id?: number } };
}

interface VerifyResponse {
  readonly ok: boolean;
  readonly tg_user_id: number;
  readonly first_name: string | null;
  readonly last_name: string | null;
  readonly username: string | null;
  readonly language_code: string | null;
  readonly is_premium: boolean;
}

interface CachedTgIdentity {
  readonly tg_user_id: number;
  readonly first_name: string | null;
  readonly username: string | null;
  readonly verified_at: number;
}

const STORAGE_KEY = 'sai-tg-id-v1';
/** Re-verify не чаще раза в сутки — initData каждый раз новый, но мы */
/** доверяем уже verified identity 24h чтобы не дёргать /auth/tg на каждый mount. */
const RECHECK_INTERVAL_MS = 24 * 60 * 60 * 1000;

function getTgWebApp(): TgWebAppGlobal | null {
  if (typeof window === 'undefined') return null;
  const tg = (window as unknown as { Telegram?: { WebApp?: TgWebAppGlobal } }).Telegram;
  const webApp = tg?.WebApp;
  if (webApp === undefined) return null;
  // initData может быть пустой строкой если открыто как обычный URL без TG.
  if (typeof webApp.initData !== 'string' || webApp.initData.length === 0) return null;
  return webApp;
}

function readCached(): CachedTgIdentity | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as CachedTgIdentity;
    if (typeof parsed.tg_user_id !== 'number') return null;
    if (typeof parsed.verified_at !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCached(identity: CachedTgIdentity): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
  } catch {
    // Sandbox / private mode — silent fail, верифицируем при следующем mount.
  }
}

function getApiBase(): string | null {
  const base = import.meta.env.VITE_API_BASE ?? '';
  if (base.length === 0) return null;
  return base.replace(/\/$/, '');
}

/**
 * Получить cached identity (или null если ещё не verified / expired).
 * Idempotent — UI компоненты могут вызывать сколько хотят.
 */
export function getTgIdentity(): CachedTgIdentity | null {
  return readCached();
}

/**
 * Verify TG WebApp identity once. Идемпотентно — если в cache свежий
 * verified identity, skip network request.
 *
 * Не throws. Тихо log'ит warnings.
 */
let verifyInProgress = false;
export async function verifyTgIdentityOnce(): Promise<CachedTgIdentity | null> {
  if (verifyInProgress) return getTgIdentity();
  const cached = readCached();
  if (cached !== null && Date.now() - cached.verified_at < RECHECK_INTERVAL_MS) {
    return cached;
  }
  const webApp = getTgWebApp();
  if (webApp === null) return null;
  const base = getApiBase();
  if (base === null) return null;

  verifyInProgress = true;
  try {
    const res = await fetch(`${base}/auth/tg`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ init_data: webApp.initData }),
    });
    if (res.status === 503) {
      // Bot не configured — silent skip. JB поставит BOT_TOKEN позже.
      return null;
    }
    if (!res.ok) {
      console.warn('[tg-auth] verify failed:', res.status);
      return null;
    }
    const data = (await res.json()) as VerifyResponse;
    if (!data.ok || typeof data.tg_user_id !== 'number') return null;
    const identity: CachedTgIdentity = {
      tg_user_id: data.tg_user_id,
      first_name: data.first_name,
      username: data.username,
      verified_at: Date.now(),
    };
    writeCached(identity);
    return identity;
  } catch (err) {
    console.warn('[tg-auth] verify error:', err);
    return null;
  } finally {
    verifyInProgress = false;
  }
}
